/**
 * SwipeCard — extracted from Discover.jsx (Feb 2026).
 *
 * Owns:
 *   - photoIdx (hero carousel index — resets to 0 when a new card mounts)
 *   - Framer Motion drag values (x, rotate, likeOpacity, nopeOpacity)
 *   - Drag-end threshold → onSwipe('left' | 'right')
 *   - The vertical scroller inside the card + all FullProfileBody
 *     inline slot renderers.
 *
 * Delegates to the parent via callbacks:
 *   - onSwipe(dir), onUndo, onReport, onBlock, onOpenLightbox(idx)
 *
 * The parent still owns swipe economy state (swipeInfo, breakdown, etc.)
 * and passes them in as props. The parent's `scrollRef` is attached to the
 * inner scroller so the parent can save/restore scroll position around
 * Undo (see saveCurrentScroll in Discover.jsx).
 */
import { motion, useMotionValue, useTransform } from 'framer-motion';
import { useState, useRef, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  X, Heart, MapPin, Star, Undo2, CheckCircle2, Crown, Send,
  ChevronLeft, ChevronRight, Sparkles,
} from 'lucide-react';
import { CredibilityBadge } from './CredibilityBadge';
import VerifiedBadge from './VerifiedBadge';
import DemoProfileBadge from './DemoProfileBadge';
import FullProfileBody from './FullProfileBody';
import { formatDistance as fmtDist, getUnit } from '@/utils/units';

const BADGE_CFG = {
  intentional_dater: { label: 'Intentional', cls: 'badge-intent' },
  high_compatibility: { label: 'High Match', cls: 'badge-match' },
  complete_profile: { label: 'Complete', cls: 'badge-complete' },
};

function getPhotoUrl(p) {
  if (!p) return null;
  if (p.startsWith('http')) return p;
  return `${window.location.origin}/api/files/${p}`;
}

/**
 * Red heart that fills bottom-up based on compatibility percentage.
 * Kept co-located with SwipeCard since it's the only consumer.
 */
function CompatRing({ score, size = 56 }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const fillPct = s / 100;
  const HEART_TOP = 5;
  const HEART_H = 16;
  const fillH = fillPct * HEART_H;
  const fillY = HEART_TOP + (HEART_H - fillH);
  const clipId = `heart-clip-${size}`;
  return (
    <div className="relative inline-flex items-center justify-center" style={{width:size, height:size}} data-testid="compat-ring">
      <svg viewBox="0 0 24 22" width={size} height={size} className="drop-shadow-[0_1px_2px_rgba(0,0,0,0.25)]">
        <defs>
          <clipPath id={clipId}>
            <path d="M12 21s-8-4.5-8-11a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 6.5-8 11-8 11h-2z"/>
          </clipPath>
          <linearGradient id={`heartGrad-${size}`} x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#991B1B"/>
            <stop offset="100%" stopColor="#EF4444"/>
          </linearGradient>
        </defs>
        <path d="M12 21s-8-4.5-8-11a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 6.5-8 11-8 11h-2z"
          fill="rgba(255,255,255,0.18)" stroke="#fff" strokeWidth="1.2" strokeLinejoin="round"/>
        <rect x="0" y={fillY} width="24" height={fillH}
          fill={`url(#heartGrad-${size})`} clipPath={`url(#${clipId})`} />
      </svg>
      <span className="absolute font-display font-[700] text-white" style={{fontSize: size * 0.22, textShadow:'0 1px 2px rgba(0,0,0,0.5)'}}>{s}%</span>
    </div>
  );
}

export default function SwipeCard({
  profile,
  swipeInfo,
  breakdown,
  promptsList,
  swiping,
  scrollRef: _legacyScrollRef,   // Deprecated — kept in the signature so
                                 // Discover.jsx doesn't need touching in
                                 // the same hotfix. Ignored below.
  onSwipe,
  onUndo,
  onIntro,    // Feb 2026 — Aug parity: opens SendMessageDialog bottom sheet
  onReport,
  onBlock,
  onOpenLightbox,
}) {
  const navigate = useNavigate();
  const [photoIdx, setPhotoIdx] = useState(0);

  // Feb 2026 hotfix — SwipeCard owns its own scroll ref now. Previously
  // the parent Discover passed one down, but during the 280ms
  // AnimatePresence exit both the outgoing AND incoming cards bind to
  // the same ref → whichever mounts second wins → parent's scroll-reset
  // useEffect touches the wrong DOM node, leaving new cards
  // mid-scrolled ("out of whack"). By owning the ref internally and
  // resetting synchronously in useLayoutEffect we guarantee the new
  // card renders with hero-first.
  const cardScrollRef = useRef(null);
  useLayoutEffect(() => {
    if (cardScrollRef.current) cardScrollRef.current.scrollTop = 0;
    setPhotoIdx(0);   // also snap the hero carousel back to photo 0
  }, [profile?.user_id]);

  // Drag motion values — deck rotation ±6°.
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-300, 300], [-6, 6]);
  const likeOpacity = useTransform(x, [0, 120], [0, 1]);
  const nopeOpacity = useTransform(x, [-120, 0], [1, 0]);

  const handleDragEnd = (_, info) => {
    const threshold = 120;
    if (info.offset.x > threshold) onSwipe('right');
    else if (info.offset.x < -threshold) onSwipe('left');
    else x.set(0);
  };

  // Feb 2026 hotfix — button-tap swipes.
  // When the user taps Like / Nope / Rose / Intro, x is still 0, so the
  // AnimatePresence exit fell through to the fade-in-place branch (see
  // `exit` prop below) — the card just disappeared without a direction cue,
  // which read as "twitching" during the 280ms hand-off to the next card.
  // Priming x with a directional value BEFORE calling onSwipe makes the
  // exit branch pick the correct off-screen slide (left/right).
  const handleButtonSwipe = (dir) => {
    // Rose ("super") and Intro both count as a right-swipe visually — they
    // send positive intent and should slide the card off to the right like
    // a Like. Only "left" (Pass) slides left.
    if (dir === 'left') x.set(-300);
    else x.set(300);
    onSwipe(dir);
  };

  const cur = profile;
  const slides = [
    ...((cur.videos || []).filter(Boolean).slice(0, 1).map(v => ({ kind: 'video', src: v }))),
    ...((cur.photos?.length ? cur.photos : [cur.photo_url]).filter(Boolean).map(p => ({ kind: 'photo', src: p }))),
  ];
  const photos = slides.map(s => s.src);

  return (
    <motion.div
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={handleDragEnd}
      style={{ x, rotate, touchAction: 'pan-y' }}
      initial={{ scale: 0.96, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ x: x.get() > 0 ? 600 : x.get() < 0 ? -600 : 0, opacity: 0, transition: { duration: 0.28 } }}
      transition={{ duration: 0.22, ease: [0.2, 0.8, 0.2, 1] }}
      className="relative rounded-[28px] overflow-hidden elevated-card select-none bg-white"
      data-testid="profile-card"
    >
      <div
        ref={cardScrollRef}
        className="w-full overflow-y-auto overflow-x-hidden overscroll-contain"
        style={{
          height: 'calc(100dvh - 400px)',
          maxHeight: '560px',
          minHeight: '380px',
          WebkitOverflowScrolling: 'touch',
        }}
        data-testid="card-scroller"
      >
        {/* Hero */}
        <div className="relative" style={{ height: 'calc(100dvh - 400px)', maxHeight: '560px', minHeight: '380px' }}>
          {slides[photoIdx]?.kind === 'video' ? (
            <video
              key={`${cur.user_id}-vid-${photoIdx}`}
              src={getPhotoUrl(slides[photoIdx].src)}
              className="w-full h-full object-cover pointer-events-none"
              autoPlay muted loop playsInline preload="metadata"
              data-testid="discover-card-video"
            />
          ) : (
            <img
              src={getPhotoUrl(photos[photoIdx]) || `https://api.dicebear.com/7.x/avataaars/svg?seed=${cur.user_id}`}
              alt="" className="w-full h-full object-cover pointer-events-none" draggable={false}
              onError={e => { e.target.src = `https://api.dicebear.com/7.x/avataaars/svg?seed=${cur.user_id}`; }}
            />
          )}

          {slides[photoIdx]?.kind === 'video' && (
            <div className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/55 backdrop-blur text-white text-[10px] font-bold uppercase tracking-[1.3px]" data-testid="discover-video-badge">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
              Video
            </div>
          )}

          {photos.length > 1 && (
            <div className="absolute top-3 left-3 right-3 flex gap-1 z-10">
              {photos.map((_, i) => (
                <div key={i} className={`h-0.5 flex-1 rounded-full transition-all duration-300 ${i === photoIdx ? 'bg-white' : 'bg-white/30'}`} />
              ))}
            </div>
          )}

          {photos.length > 1 && (
            <>
              {photoIdx > 0 && (
                <button data-photo-nav
                  onClick={(e) => { e.stopPropagation(); setPhotoIdx(p => Math.max(0, p - 1)); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/45 backdrop-blur flex items-center justify-center text-white shadow-[0_2px_8px_rgba(0,0,0,0.25)] active:scale-90 transition-transform"
                  data-testid="photo-prev" aria-label="Previous photo">
                  <ChevronLeft size={20} strokeWidth={2.4}/>
                </button>
              )}
              {photoIdx < photos.length - 1 && (
                <button data-photo-nav
                  onClick={(e) => { e.stopPropagation(); setPhotoIdx(p => Math.min(photos.length - 1, p + 1)); }}
                  onPointerDown={(e) => e.stopPropagation()}
                  className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-black/45 backdrop-blur flex items-center justify-center text-white shadow-[0_2px_8px_rgba(0,0,0,0.25)] active:scale-90 transition-transform"
                  data-testid="photo-next" aria-label="Next photo">
                  <ChevronRight size={20} strokeWidth={2.4}/>
                </button>
              )}
            </>
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent pointer-events-none" />

          <div className="absolute bottom-0 left-0 right-0 px-6 pt-6 z-10 pointer-events-none" style={{ paddingBottom: 100 }}>
            <div className="flex items-end justify-between mb-3">
              <div>
                <div className="flex gap-1.5 mb-2 flex-wrap">
                  {cur.is_seed && <DemoProfileBadge isSeed={true} size="sm" />}
                  {/* Feb 27 2026 · Phase 165 — "New" ribbon for profiles joined
                      in the last 14 days. Reinforces the value of the
                      freshly-wired VIP `new_members` filter for Free/Premium
                      users too. */}
                  {(() => {
                    if (!cur.created_at) return null;
                    try {
                      const ageDays = (Date.now() - new Date(cur.created_at).getTime()) / 86400000;
                      if (ageDays < 0 || ageDays > 14) return null;
                      return (
                        <span
                          data-testid={`swipe-new-badge-${cur.user_id}`}
                          className="badge bg-gradient-to-r from-[#B91C1C] to-[#EF4444] text-white border-[#B91C1C]/40 shadow-sm">
                          <Sparkles size={9} strokeWidth={2.4} className="inline mr-0.5 -mt-px" />
                          New
                        </span>
                      );
                    } catch { return null; }
                  })()}
                  {cur.is_boosted && <span className="badge gold-gradient text-white border-0" data-testid="badge-boosted">⚡ Meet Priority</span>}
                  {cur.is_online && <span className="badge inline-flex items-center gap-1" style={{background:'rgba(22,163,74,0.15)', color:'#16A34A', border:'1px solid rgba(22,163,74,0.3)'}} data-testid="badge-online"><span className="w-1.5 h-1.5 rounded-full bg-[#16A34A] animate-pulse"/>Online</span>}
                  {(cur.badges || []).filter(b => b !== 'intentional_dater' && b !== 'complete_profile').slice(0,3).map(b => { const c = BADGE_CFG[b]; return c ? <span key={b} className={`badge ${c.cls}`} data-testid={`badge-${b}`}>{c.label}</span> : null; })}
                </div>
                {(cur.tier === 'ready' || cur.credibility?.tier) && (
                  <div className="flex gap-1.5 mb-2 flex-wrap">
                    {cur.tier === 'ready' && (
                      <span data-testid="tier-ready"
                        className="badge bg-gradient-to-r from-[#15803D] to-[#166534] text-white border-[#166534]">
                        🔥 Ready to Meet
                      </span>
                    )}
                    {cur.credibility?.tier && (
                      <CredibilityBadge credibility={cur.credibility} size="sm" showScore={true} />
                    )}
                  </div>
                )}
                <h2 className="font-display text-3xl font-[600] text-white tracking-tight leading-none inline-flex items-center gap-2" data-testid="profile-name">
                  <span>{cur.display_name}, {cur.age}</span>
                  <VerifiedBadge badges={cur.badges} size={20} data-testid="verified-checkmark" />
                </h2>
                {/* Feb 28 2026 · Discover Shared Interests contract alignment
                    with the mobile team's Feb 28 handoff. Placement: below
                    the name+age line, above the location row. testID +
                    aria-label carry the FULL list (max 5) — the visual copy
                    truncates to 3 with +N overflow. Sparkles icon is
                    aria-hidden. Skip render entirely when empty. */}
                {Array.isArray(cur.shared_interests) && cur.shared_interests.length > 0 && (
                  <p className="text-[11px] text-white/85 mt-1.5 flex items-center gap-1.5"
                     data-testid="discover-shared-interests"
                     aria-label={`You both like ${cur.shared_interests.join(', ')}`}>
                    <Sparkles size={11} className="text-[#FDE68A]" strokeWidth={2.4} aria-hidden="true" />
                    <span className="truncate">
                      You both like <span className="font-semibold text-white">{cur.shared_interests.slice(0, 3).join(', ')}</span>
                      {cur.shared_interests.length > 3 ? <span className="text-white/70"> +{cur.shared_interests.length - 3}</span> : null}
                    </span>
                  </p>
                )}
                {(cur.location?.city || cur.distance_km != null) && (
                  <p className="text-white/75 text-sm flex items-center gap-1.5 mt-1.5">
                    <MapPin size={12}/>
                    {cur.location?.city && <span>{cur.location.city}</span>}
                    {cur.distance_km != null && (
                      <>
                        {cur.location?.city && <span className="opacity-50">·</span>}
                        <span className="font-semibold text-white/90" data-testid="profile-distance">
                          {cur.distance_km < 1.6 ? 'Less than 1 mi away' : `${fmtDist(cur.distance_km)} away`}
                        </span>
                      </>
                    )}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <CompatRing score={cur.compatibility_score || 50} />
                {cur.match_score != null && (
                  <div data-testid="meet-score-badge"
                    title="Meet Score — how likely you two are to actually meet up, out of 100"
                    className="flex flex-col items-center justify-center px-2.5 py-1.5 rounded-xl bg-gradient-to-b from-[#B91C1C] to-[#7F1D1D] border border-[#7F1D1D] text-white shadow-[0_2px_8px_rgba(185,28,28,0.4)]">
                    <span className="text-[8px] font-bold tracking-[0.15em] uppercase opacity-90">Meet</span>
                    <span className="text-base font-display font-[700] tabular-nums leading-none mt-0.5">{cur.match_score}</span>
                  </div>
                )}
              </div>
            </div>
            {cur.bio && <p className="text-white/80 text-[13px] leading-relaxed line-clamp-2 font-display italic">&ldquo;{cur.bio}&rdquo;</p>}
            <div className="flex items-center gap-2 mt-3 opacity-70">
              <div className="w-1 h-1 rounded-full bg-white animate-pulse" />
              <p className="text-[10px] tracking-[0.15em] uppercase text-white/70" data-testid="scroll-hint">Pull up to see profile · Swipe to decide</p>
            </div>
          </div>
        </div>

        {/* Full profile body */}
        <div className="bg-white px-4 pt-6 pb-32" data-testid="inline-profile-body">
          <FullProfileBody
            hideHero={true}
            profile={{ ...cur, photos: photos.map(p => getPhotoUrl(p)).filter(Boolean) }}
            compatScore={cur.compatibility_score || 0}
            meetScore={cur.match_score || 0}
            meetTier={cur.tier}
            distanceKm={cur.distance_km}
            useMetric={getUnit() === 'metric'}
            promptsList={promptsList}
            onPhotoOpen={(i) => onOpenLightbox?.(i)}
            onCompatOpen={() => {
              const el = document.querySelector('[data-testid="compat-breakdown-anchor"]');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            onMeetScoreOpen={() => {
              if (swipeInfo?.tier === 'free') navigate('/subscription');
            }}
            onReport={onReport}
            onBlock={onBlock}
            renderBadgeRow={() => (
              ((cur.badges || []).length > 0 || cur.is_seed) ? (
                <div className="flex flex-wrap gap-1.5" data-testid="detail-badges">
                  {cur.is_seed && <DemoProfileBadge isSeed={true} size="sm" />}
                  {(cur.badges || []).map(b => { const c = BADGE_CFG[b]; return c ? <span key={b} className={`badge ${c.cls}`}>{c.label}</span> : null; })}
                </div>
              ) : null
            )}
            renderMeetScorePanel={() => (
              cur.match_score !== undefined ? (
                <div className="rounded-2xl border border-[var(--hairline)] p-4 bg-gradient-to-br from-white to-[#FAFAFA]" data-testid="match-score-section">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <p className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A93] font-semibold">Meet score</p>
                      <p className="font-display text-2xl font-[700] mt-0.5">
                        <span className={
                          cur.tier === 'ready' ? 'text-[#15803D]' :
                          cur.tier === 'good' ? 'text-[#3B82F6]' : 'text-[#4B4B52]'
                        }>{cur.match_score}</span>
                        <span className="text-[#8A8A93] text-base">/100</span>
                      </p>
                    </div>
                    <span className={`badge ${
                      cur.tier === 'ready' ? 'bg-gradient-to-r from-[#15803D] to-[#166534] text-white border-[#166534]'
                      : cur.tier === 'good' ? 'bg-gradient-to-r from-[#3B82F6] to-[#1E40AF] text-white border-[#1E40AF]'
                      : 'bg-black/40 text-white border-white/20'
                    }`}>
                      {cur.tier === 'ready' ? '🔥 Ready to Meet' : cur.tier === 'good' ? '🎯 Good Fit' : '💬 Casual'}
                    </span>
                  </div>
                  {swipeInfo?.tier !== 'free' && cur.match_breakdown ? (
                    <div className="space-y-1.5">
                      {[
                        ['meeting_readiness', 'Meeting readiness'],
                        ['location', 'Distance & travel'],
                        ['intent', 'Intent alignment'],
                        ['lifestyle', 'Lifestyle match'],
                        ['activity', 'Activity level'],
                      ].map(([k, label]) => {
                        const b = cur.match_breakdown[k] || { score: 0, max: 1 };
                        const max = b.max && b.max > 0 ? b.max : 1;
                        const pct = Math.round((b.score / max) * 100);
                        return (
                          <div key={k} className="flex items-center gap-3 text-[11px]">
                            <span className="text-[#4B4B52] flex-1">{label}</span>
                            <div className="flex-[2] h-1.5 bg-black/[0.05] rounded-full overflow-hidden">
                              <div className="h-full brand-gradient transition-all" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="w-12 text-right tabular-nums font-semibold text-[#0F0F10]">{b.score}/{b.max}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <button onClick={() => navigate('/subscription')} data-testid="unlock-meet-score-cta"
                      className="w-full mt-1 py-2 rounded-full text-[11px] font-bold gold-gradient text-white hover:scale-[1.01] transition inline-flex items-center justify-center gap-1.5">
                      <Crown size={11} /> Unlock score breakdown
                    </button>
                  )}
                </div>
              ) : null
            )}
            renderMeetingPrefs={() => (
              cur.meeting_prefs && Object.keys(cur.meeting_prefs).length > 0 ? (
                <div data-testid="profile-meeting-prefs">
                  <p className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A93] font-semibold mb-3">Meeting style</p>
                  <div className="flex flex-wrap gap-1.5">
                    {cur.meeting_prefs.intent && (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold border capitalize"
                        style={{ background: 'rgba(30,79,216,0.08)', color: '#1E4FD8', borderColor: 'rgba(30,79,216,0.25)' }}>
                        {String(cur.meeting_prefs.intent).replace(/_/g, ' ')}
                      </span>
                    )}
                    {cur.meeting_prefs.meeting_readiness && (
                      <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize ${
                        cur.meeting_prefs.meeting_readiness === 'yes'
                          ? 'bg-[#15803D]/[0.08] text-[#15803D] border border-[#15803D]/20'
                          : cur.meeting_prefs.meeting_readiness === 'maybe'
                            ? 'bg-[#FDF7EC] text-[#B38138] border border-[#B38138]/25'
                            : 'bg-black/[0.04] text-[#8A8A93] border border-black/10'
                      }`}>
                        {cur.meeting_prefs.meeting_readiness === 'yes' ? '🟢 Open this week'
                          : cur.meeting_prefs.meeting_readiness === 'maybe' ? '🟡 Maybe this week'
                          : 'Not meeting now'}
                      </span>
                    )}
                    {cur.meeting_prefs.meeting_style && (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-[var(--hairline-strong)] text-[#4B4B52] capitalize">
                        Meets {String(cur.meeting_prefs.meeting_style).replace(/_/g, ' ')}
                      </span>
                    )}
                    {Array.isArray(cur.meeting_prefs.availability) && cur.meeting_prefs.availability.map(v => (
                      <span key={v} className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-[var(--hairline-strong)] text-[#4B4B52] capitalize">
                        {v.replace(/_/g, ' ')}
                      </span>
                    ))}
                    {Array.isArray(cur.meeting_prefs.date_type_prefs) && cur.meeting_prefs.date_type_prefs.map(v => (
                      <span key={v} className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-[var(--hairline-strong)] text-[#4B4B52] capitalize">
                        {v}
                      </span>
                    ))}
                    {cur.meeting_prefs.ideal_first_plan && (
                      <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-white border border-[var(--hairline-strong)] text-[#4B4B52] capitalize">
                        1st: {String(cur.meeting_prefs.ideal_first_plan)}
                      </span>
                    )}
                  </div>
                </div>
              ) : null
            )}
            renderCompatibilityCard={() => (
              breakdown && (breakdown.matches.length > 0 || breakdown.differences.length > 0) ? (
                <div data-testid="compat-breakdown" className="bg-white border border-[var(--hairline)] rounded-2xl p-5">
                  <div data-testid="compat-breakdown-anchor" />
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-[10px] tracking-[0.2em] uppercase text-[#8A8A93] font-semibold mb-1">Compatibility</p>
                      <p className="font-display text-sm text-[#0F0F10]">
                        <span className="font-[700] brand-gradient-text text-lg">{breakdown.overall_score}%</span>
                        <span className="ml-1">with {breakdown.other_name}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-3 text-[11px]">
                      <div className="text-center"><p className="font-[700] text-[#16A34A] text-base leading-none">{breakdown.match_count}</p><p className="text-[#8A8A93] uppercase tracking-wider mt-0.5">Match</p></div>
                      <div className="text-center"><p className="font-[700] text-[var(--brand-blue)] text-base leading-none">{breakdown.diff_count}</p><p className="text-[#8A8A93] uppercase tracking-wider mt-0.5">Differ</p></div>
                    </div>
                  </div>
                  {breakdown.matches.length > 0 && (
                    <div className="mb-3">
                      <p className="text-[10px] tracking-[0.15em] uppercase text-[#16A34A] font-bold mb-2">You align on</p>
                      <div className="space-y-1.5">
                        {breakdown.matches.map((m, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            <div className="w-4 h-4 rounded-full bg-[#DCFCE7] flex items-center justify-center flex-shrink-0 mt-0.5">
                              <CheckCircle2 size={11} className="text-[#16A34A]" strokeWidth={2.5}/>
                            </div>
                            <div className="flex-1">
                              <span className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-semibold">{m.question_label}:</span>
                              <span className="ml-1.5 text-[#0F0F10] font-medium">{m.answer_label}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {breakdown.differences.length > 0 && (
                    <div>
                      <p className="text-[10px] tracking-[0.15em] uppercase text-[var(--brand-blue)] font-bold mb-2">Different, but could work</p>
                      <div className="space-y-1.5">
                        {breakdown.differences.map((d, i) => (
                          <div key={i} className="flex items-start gap-2 text-sm">
                            <div className="w-4 h-4 rounded-full bg-[#EFF4FF] flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-[10px] text-[var(--brand-blue)] font-bold">~</span>
                            </div>
                            <div className="flex-1">
                              <span className="text-[11px] text-[#8A8A93] uppercase tracking-wider font-semibold">{d.question_label}:</span>
                              <span className="ml-1.5 text-[#0F0F10] font-medium">You: {d.your_answer_label}</span>
                              <span className="text-[#8A8A93]"> · Them: {d.their_answer_label}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : null
            )}
          />
        </div>
      </div>

      {/* LIKE / NOPE overlays */}
      <motion.div style={{ opacity: likeOpacity }} className="absolute top-10 left-6 z-30 pointer-events-none">
        <div className="rotate-[-10deg] border-[3px] border-[#B91C1C] rounded-lg px-5 py-1.5 bg-white/10 backdrop-blur-sm">
          <span className="font-display-tight text-3xl font-[700] tracking-wider text-[#B91C1C]">LIKE</span>
        </div>
      </motion.div>
      <motion.div style={{ opacity: nopeOpacity }} className="absolute top-10 right-6 z-30 pointer-events-none">
        <div className="rotate-[10deg] border-[3px] border-white rounded-lg px-5 py-1.5 bg-white/10 backdrop-blur-sm">
          <span className="font-display-tight text-3xl font-[700] tracking-wider text-white">NOPE</span>
        </div>
      </motion.div>

      {/* Floating action bar */}
      <div
        data-testid="card-action-bar"
        onPointerDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        className="absolute left-0 right-0 z-30 flex items-center justify-center gap-3.5 px-4"
        style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)' }}
      >
        <button onClick={onUndo} disabled={swiping || !swipeInfo?.can_undo} data-testid="undo-btn"
          title={swipeInfo?.can_undo ? 'Undo last swipe' : 'Premium feature — upgrade to undo'}
          className="w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-90 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_2px_10px_rgba(0,0,0,0.15)]"
          style={{ background: '#FFFFFF', color: 'var(--parity-undo)' }}>
          <Undo2 size={20} strokeWidth={2.4} />
        </button>
        <button onClick={() => handleButtonSwipe('left')} disabled={swiping} data-testid="swipe-left-btn"
          title="Pass"
          className="w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-90 hover:scale-105 shadow-[0_2px_10px_rgba(0,0,0,0.15)]"
          style={{ background: '#FFFFFF', color: 'var(--parity-ink)' }}>
          <X size={30} strokeWidth={2.6} />
        </button>
        <button onClick={() => handleButtonSwipe('right')} disabled={swiping || (swipeInfo && !swipeInfo.can_swipe_right)} data-testid="swipe-right-btn"
          title="Like"
          className="w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-90 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_2px_10px_rgba(0,0,0,0.15)]"
          style={{ background: '#FFFFFF', color: 'var(--parity-like)' }}>
          <Heart size={26} fill="currentColor" strokeWidth={2.4} />
        </button>
        <button onClick={() => handleButtonSwipe('super')} disabled={swiping || (swipeInfo && swipeInfo.super_likes_remaining <= 0)} data-testid="swipe-super-btn"
          title={`Serious Interest · ${swipeInfo?.super_likes_remaining ?? 0} left this week`}
          className="relative w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-90 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_2px_10px_rgba(0,0,0,0.15)]"
          style={{ background: '#FFFFFF', color: 'var(--parity-super)' }}>
          <Star size={24} fill="currentColor" />
          {swipeInfo && swipeInfo.super_likes_remaining > 0 && swipeInfo.super_likes_remaining < 999 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center border border-white" style={{ background: 'var(--parity-ink)' }}>{swipeInfo.super_likes_remaining}</span>
          )}
        </button>
        {/* Feb 2026 (Aug 2026 mobile parity) — direct intro / message button.
            Opens the Hinge/Bumble-style bottom-sheet composer. Only rendered
            when the parent passes onIntro; falls back gracefully otherwise. */}
        {onIntro && (
          <button onClick={onIntro} disabled={swiping} data-testid="discover-intro-btn"
            title="Send a message"
            className="w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-90 hover:scale-105 disabled:opacity-40 disabled:cursor-not-allowed shadow-[0_2px_10px_rgba(0,0,0,0.15)]"
            style={{ background: '#FFFFFF', color: '#2563EB' }}>
            <Send size={20} strokeWidth={2.4} />
          </button>
        )}
      </div>
    </motion.div>
  );
}

// Re-export helpers for the (rare) consumer that might need them.
export { getPhotoUrl, CompatRing };
